// lib/I18nProvider.tsx
'use client';

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import en from '@/locales/en';
import fr from '@/locales/fr';

const translations = {
  en,
  fr,
};

const I18nContext = createContext(undefined);

function normalizeLocale(value) {
  return value === 'en' || value === 'fr' ? value : null;
}

function localeFromSearch(search) {
  if (!search) return null;
  return normalizeLocale(new URLSearchParams(search).get('lang'));
}

function writeLangQueryParam(next) {
  const url = new URL(window.location.href);
  if (url.searchParams.get('lang') === next) return;
  url.searchParams.set('lang', next);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

export function I18nProvider({ children }) {
  const [locale, setLocale] = useState('fr');

  // Query ?lang=en|fr wins over localStorage so shared links can force a language.
  useEffect(() => {
    const fromQuery = localeFromSearch(window.location.search);
    const storedLocale = normalizeLocale(localStorage.getItem('locale'));
    const next = fromQuery ?? storedLocale;
    if (!next) return;
    setLocale(next);
    if (fromQuery) {
      localStorage.setItem('locale', fromQuery);
    }
  }, []);

  const persistLocale = useCallback((next) => {
    if (next !== 'en' && next !== 'fr') return;
    setLocale(next);
    if (typeof window !== 'undefined') {
      localStorage.setItem('locale', next);
      writeLangQueryParam(next);
    }
  }, []);

  const interpolate = (template, params) => {
    if (!params || typeof template !== 'string') {
      return template;
    }
    let result = template;
    for (const [paramKey, paramValue] of Object.entries(params)) {
      const replacement = String(paramValue ?? '');
      result = result.replaceAll(`{{${paramKey}}}`, replacement);
      result = result.replaceAll(`{${paramKey}}`, replacement);
    }
    return result;
  };

  const t = (key, params) => {
    const keys = key.split('.');
    let value = translations[locale];
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return key;
      }
    }
    return interpolate(value, params);
  };

  return (
    <I18nContext.Provider value={{ locale, t, setLocale: persistLocale }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}
